/*
  Spell correction for the blog search, built on nspell with the British
  English Hunspell dictionary.

  Nothing is downloaded until someone focuses the search field; the dictionary
  (~186KB compressed) is then fetched and built during idle time, so it is
  normally ready before they finish typing.

  Correction is only ever attempted when a search finds nothing, and only a
  correction that itself finds articles is used — so a word that already
  returns results is never touched, and a suggestion leading nowhere is never
  shown.
*/
(function () {
  // Skincare and brand words a general dictionary does not know. Without them
  // it would offer "retinal" for "retinol". nspell adds exact words only, so
  // plurals are listed too. The Blog section setting adds more.
  var TERMS = [
    'neoderma', 'airexo', 'exosome', 'exosomes',
    'niacinamide', 'hyaluronic', 'retinol', 'retinoid', 'retinoids', 'retinal',
    'peptide', 'peptides', 'ceramide', 'ceramides', 'squalane', 'bakuchiol',
    'panthenol', 'allantoin', 'glycerin', 'centella', 'azelaic', 'salicylic',
    'glycolic', 'lactic', 'mandelic', 'tranexamic', 'collagen', 'elastin',
    'antioxidant', 'antioxidants', 'microbiome', 'sebum', 'comedogenic',
    'noncomedogenic', 'couperose', 'rosacea', 'hyperpigmentation',
    'serum', 'serums', 'sunscreen', 'sunscreens', 'spf', 'moisturiser',
    'moisturisers', 'moisturizer', 'moisturizers', 'cleanser', 'cleansers',
    'toner', 'toners', 'exfoliant', 'exfoliants', 'exfoliation', 'exfoliating',
    'dermatologist', 'dermatological', 'airless', 'vegan', 'skincare',
    // Everyday words the dictionary knows but ranks poorly for short typos —
    // it offers "sin" and "sky" for "skn", never "skin".
    'skin', 'face', 'eye', 'eyes', 'lip', 'lips', 'acne', 'dry', 'oily',
    'sensitive', 'combination', 'wrinkle', 'wrinkles', 'ageing', 'aging',
    'pore', 'pores', 'blemish', 'blemishes', 'redness', 'hydration',
    'hydrating', 'dehydrated', 'barrier', 'glow', 'radiance', 'brightening',
    'firming', 'plump', 'cream', 'creams', 'gel', 'mask', 'oil', 'routine',
    'ingredient', 'ingredients', 'beauty', 'hygiene'
  ];

  var building = null;
  // TERMS plus the section's extra words; checked before the dictionary's own
  // suggestions, so a skincare word always wins over a general one.
  var vocabulary = [];

  // Edit distance (optimal string alignment: insert, delete, substitute, swap
  // two neighbours). Stops early once it cannot come in under `max`.
  function distance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var d = [];
    var i, j;
    for (i = 0; i <= a.length; i++) d[i] = [i];
    for (j = 0; j <= b.length; j++) d[0][j] = j;
    for (i = 1; i <= a.length; i++) {
      var rowMin = Infinity;
      for (j = 1; j <= b.length; j++) {
        var cost = a[i - 1] === b[j - 1] ? 0 : 1;
        var value = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          value = Math.min(value, d[i - 2][j - 2] + 1);
        }
        d[i][j] = value;
        if (value < rowMin) rowMin = value;
      }
      if (rowMin > max) return max + 1;
    }
    return d[a.length][b.length];
  }

  // Vocabulary words within reach of a typo: one edit for short words, two
  // for longer ones. Closest first.
  function nearby(word) {
    var max = word.length <= 4 ? 1 : 2;
    return vocabulary
      .map(function (term) { return { term: term, d: distance(word, term, max) }; })
      .filter(function (item) { return item.d <= max; })
      .sort(function (a, b) { return a.d - b.d; })
      .map(function (item) { return item.term; });
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function idle() {
    return new Promise(function (resolve) {
      if ('requestIdleCallback' in window) window.requestIdleCallback(resolve, { timeout: 1500 });
      else setTimeout(resolve, 1);
    });
  }

  // One shared build per page. A failure clears it so a later search can try
  // again after a network blip.
  function ready(config) {
    if (building) return building;
    building = Promise.all([loadScript(config.lib), loadScript(config.dict)])
      .then(idle)
      .then(function () {
        var spell = window.nspell(window.NeoDictionaryEnGB);
        vocabulary = TERMS.concat(config.extra || [])
          .map(function (word) { return String(word || '').trim().toLowerCase(); })
          .filter(function (word, i, all) { return word && all.indexOf(word) === i; });
        vocabulary.forEach(function (word) {
          spell.add(word);
        });
        // The raw text is only needed to build; drop it from memory.
        window.NeoDictionaryEnGB = null;
        return spell;
      });
    building.catch(function () {
      building = null;
    });
    return building;
  }

  // Up to `count` corrected versions of the query, most likely first. Only
  // words the dictionary rejects are changed. Returns [] when there is nothing
  // to correct.
  function candidates(spell, query, count) {
    var words = query.toLowerCase().split(/\s+/).filter(Boolean);
    var options = words.map(function (word) {
      // Very short words and anything with digits (SPF30, 2024) are left alone.
      if (word.length < 3 || /\d/.test(word) || spell.correct(word)) return null;
      // Skincare words first, then the dictionary's. Fragments under three
      // letters are dropped — "skn" once produced "kn".
      var suggestions = nearby(word)
        .concat(spell.suggest(word).map(function (s) { return s.toLowerCase(); }))
        .filter(function (s, i, all) { return s.length >= 3 && all.indexOf(s) === i; });
      return suggestions.length ? suggestions.slice(0, count) : null;
    });

    if (!options.some(Boolean)) return [];

    var phrases = [];
    for (var k = 0; k < count; k++) {
      var phrase = words
        .map(function (word, i) {
          var list = options[i];
          if (!list) return word;
          return (list[k] || list[0]).toLowerCase();
        })
        .join(' ');
      if (phrase !== words.join(' ') && phrases.indexOf(phrase) === -1) phrases.push(phrase);
    }
    return phrases;
  }

  window.NeoSpell = { ready: ready, candidates: candidates };
})();
